import React, { useState } from "react";
import PropTypes from "prop-types";
import { Elements, ExpressCheckoutElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useDispatch, useSelector } from "react-redux";
import { checkoutFinalize } from "../../utils/checkoutFinalize";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { hasSubscriptionItems } from "../../utils/hasSubscriptionItems";
import { bundleAwareCartItems } from "../../utils/bundleAwareCartItems";
import { fnUrl } from "../../config/endpoints";

const stripePromiseExpress = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const ExpressCheckoutInner = ({
  comment,
  setIsSubmittingCheckout,
  setCheckoutError,
  affiliate,
  customerId,
  discountedTotal
}) => {
  const navigate = useAppNavigate();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const submittedPromoCodes = useSelector((state) => state.cart.submittedPromoCodes);
  const cartItems = useSelector((state) => state.cart.cartItems);
  const transformedCartItems = bundleAwareCartItems(cartItems);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMethods, setHasMethods] = useState(true);


  const handleExpressCheckout = async (event) => {
    setIsSubmittingCheckout(true);
    setCheckoutError([]);

    try {
      if (!stripe || !elements) {
        throw new Error("Stripe not loaded");
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      const shipping = event.shippingAddress?.address;
      const billing = event.billingDetails;

      const billingDetails = {
        firstName: billing.name?.split(' ')[0] || shipping?.name?.split(' ')[0] || 'Customer',
        surname: billing.name?.split(' ').slice(1).join(' ') || shipping?.name?.split(' ').slice(1).join(' ') || '-',
        email: billing.email || '-',
        phone: billing.phone || '-',
        address: shipping?.line1 || '-',
        address2: shipping?.line2 || '',
        city: shipping?.city || '-',
        state: shipping?.state || '-',
        postalCode: shipping?.postal_code || '-',
      };

      const response = await fetch(fnUrl("create-stripe-intent"), {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: transformedCartItems,
          promoCodes: submittedPromoCodes.map((p) => p.code),
          billingDetails,
          express_payment_type: true,
          clientTotal: discountedTotal
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create payment intent");
      }

      const { client_secret, intent_type, intent_id, customer_id: returnedCustomerId } = data;

      let result;
      const confirmParams = {
        return_url: `${window.location.origin}/checkout/success`,
      };

      if (intent_type === 'setup') {
        result = await stripe.confirmSetup({
          elements,
          clientSecret: client_secret,
          confirmParams,
          redirect: "if_required",
        });
      } else {
        result = await stripe.confirmPayment({
          elements,
          clientSecret: client_secret,
          confirmParams,
          redirect: "if_required",
        });
      }

      if (result.error) {
        throw new Error(result.error.message);
      }

      const intent = result.paymentIntent || result.setupIntent;

      if (!intent) {
        throw new Error('No intent returned from confirmation');
      }
      if (intent.status !== 'succeeded') {
        throw new Error(`Payment status: ${intent.status}`);
      }

      await checkoutFinalize({
        cartItems,
        transformedCartItems,
        setCheckoutError,
        values: billingDetails,
        comment,
        affiliate,
        latestPromoCodes: submittedPromoCodes,
        dispatch,
        navigate,
        paymentMethodId: intent.payment_method,
        intentId: intent.id,
        intentType: intent_type === 'setup' ? 'setup' : 'payment',
        customerId: returnedCustomerId || customerId,
      });

    } catch (err) {
      setCheckoutError([err.message || "Express checkout failed"]);
    } finally {
      setIsSubmittingCheckout(false);
    }
  };

  if (hasSubscriptionItems(cartItems) || !hasMethods) {
    return;
  }

  return (
    <div className="px-[20px] mt-4">
      {isLoading && (
        <div className="flex gap-2">
          <div className="h-[50px] w-[50%] bg-gray-200 animation-pulse rounded-15" />
          <div className="h-[50px] w-[50%] bg-gray-200 animation-pulse rounded-15" />
        </div>
      )}
      <ExpressCheckoutElement
        options={{
          emailRequired: true,
          phoneNumberRequired: true,
          shippingAddressRequired: true,
          paymentMethods: {
            link: 'never',
            applePay: 'auto',
            amazonPay: 'never'
          }
        }}
        onConfirm={handleExpressCheckout}
        onReady={(event) => {
          setIsLoading(false);
          const methods = event.availablePaymentMethods || {};
          const hasAnyMethods = Object.values(methods).some((enabled) => enabled);
          if (!hasAnyMethods) {
            setHasMethods(false);
          }
        }}
        onError={(error) => {
          setCheckoutError([error.message || "Express checkout initialization failed"]);
        }}
      />
    </div>
  );
};

const ExpressCheckout = (props) => {
  const options = {
    mode: 'payment',
    amount: props.discountedTotalCents,
    currency: 'usd',
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#6772e5',
      }
    },
    clientSecret: undefined
  };

  if (props.discountedTotalCents === 0) {
    return;
  }

  return (
    <Elements key={`express-${props.discountedTotalCents}`} stripe={stripePromiseExpress} options={options}>
      <ExpressCheckoutInner {...props} />
    </Elements>
  );
};

ExpressCheckout.propTypes = {
  comment: PropTypes.string,
  setIsSubmittingCheckout: PropTypes.func.isRequired,
  setCheckoutError: PropTypes.func.isRequired,
  affiliate: PropTypes.object,
  customerId: PropTypes.string,
  discountedTotal: PropTypes.number.isRequired,
  discountedTotalCents: PropTypes.number.isRequired
};

export default ExpressCheckout;